// Chart › Finance › Exports (`/c/{slug}/chart/finance/exports`; 11 §Planning "Exports", M5-03
// `finance.export`): the ledger rows (client view), revenue or the monthly roll-up for a range of
// months, as CSV or XLSX, each row mapped to the chart of accounts (Setup › Finance › Categories).
// The API answers with the file; the page saves it under the API's own name
// (`{kind}-{from}-{to}.{csv|xlsx}`). Restricted (the owner and finance admins); every export is
// recorded (`finance.exported`). One call on load: the caller's standing.
import { Button, NativeSelect, NativeSelectOption, Notice, SectionHeading } from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { DownloadIcon } from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import {
  FinancePage,
  FormField,
  MonthSelect,
  monthKeyOf,
  monthLabel,
  monthsAround,
  RestrictedNotice,
  saveBlob,
  useFinanceStanding,
} from "./finance-parts";

export type ExportKind = "cost_events" | "revenue_events" | "rollup";
type Format = "csv" | "xlsx";

const KINDS: Record<ExportKind, { label: string; hint: string }> = {
  cost_events: {
    label: "Ledger rows",
    hint: "Every cost row (subscriptions, usage, human time, manual rows), client view.",
  },
  revenue_events: { label: "Revenue", hint: "Every revenue row, corrections included." },
  rollup: {
    label: "Roll-up",
    hint: "Per month, per company, team and seat, per kind, in the reporting currency, with pay and recurring costs.",
  },
};

/** The API's file name (`Content-Disposition`), which the SDK's Blob does not carry. */
export const exportName = (kind: ExportKind, from: string, to: string, format: Format) =>
  `${kind}-${from}-${to}.${format}`;

export function ExportsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const current = monthKeyOf(new Date());
  const months = useMemo(() => monthsAround(current, 35, 0), [current]);
  const standing = useFinanceStanding(api, company);
  const [kind, setKind] = useState<ExportKind>("cost_events");
  const [format, setFormat] = useState<Format>("csv");
  const [from, setFrom] = useState(current);
  const [to, setTo] = useState(current);
  const [saved, setSaved] = useState<{ name: string; bytes: number } | null>(null);
  const ids = { kind: useId(), format: useId(), from: useId(), to: useId() };
  const run = useMutation({
    mutationFn: () =>
      api.client.finance.export({
        params: { co: company.id },
        body: { kind, format, period_from: from, period_to: to },
      }),
    onSuccess: (blob) => {
      const name = exportName(kind, from, to, format);
      saveBlob(blob, name);
      setSaved({ name, bytes: blob.size });
    },
  });
  const reversed = from > to;

  return (
    <FinancePage company={company} where="chart" title="Exports" icon={DownloadIcon}>
      {standing.query.isPending ? (
        <Loading label="Loading your finance access" rows={2} />
      ) : standing.query.isError ? (
        <LoadError what="Your finance access" error={standing.query.error} />
      ) : !standing.restricted ? (
        <RestrictedNotice company={company} what="Exports" />
      ) : (
        <section className="flex max-w-2xl flex-col gap-4" data-slot="finance-export">
          <SectionHeading title="New export" />
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!reversed) run.mutate();
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField id={ids.kind} label="What" hint={KINDS[kind].hint}>
                <NativeSelect
                  id={ids.kind}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as ExportKind)}
                >
                  {(Object.keys(KINDS) as ExportKind[]).map((k) => (
                    <NativeSelectOption key={k} value={k}>
                      {KINDS[k].label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </FormField>
              <FormField id={ids.format} label="Format">
                <NativeSelect
                  id={ids.format}
                  value={format}
                  onChange={(e) => setFormat(e.target.value as Format)}
                >
                  <NativeSelectOption value="csv">CSV</NativeSelectOption>
                  <NativeSelectOption value="xlsx">Excel (XLSX)</NativeSelectOption>
                </NativeSelect>
              </FormField>
              <FormField id={ids.from} label="From">
                <MonthSelect id={ids.from} value={from} onChange={setFrom} months={months} />
              </FormField>
              <FormField id={ids.to} label="To" error={reversed ? "Ends before it starts." : null}>
                <MonthSelect id={ids.to} value={to} onChange={setTo} months={months} />
              </FormField>
            </div>
            <p className="text-sm text-muted-foreground">
              Rows map to the categories in{" "}
              <Link
                to="/c/$slug/setup/finance/categories"
                params={{ slug: company.slug }}
                search={keepFocus}
                className="text-primary underline-offset-4 hover:underline"
              >
                Setup › Finance › Categories
              </Link>
              . Up to 36 months at a time.
            </p>
            <div>
              <Button type="submit" disabled={reversed || run.isPending}>
                <DownloadIcon aria-hidden="true" />
                {run.isPending ? "Preparing…" : "Download"}
              </Button>
            </div>
            {run.isError ? <LoadError what="The export" error={run.error} /> : null}
            {saved && !run.isPending && !run.isError ? (
              <Notice tone="success" role="status" data-slot="export-saved">
                Saved {saved.name} ({Math.max(1, Math.round(saved.bytes / 1024))} KB),{" "}
                {monthLabel(from)}
                {from === to ? "" : ` to ${monthLabel(to)}`}.
              </Notice>
            ) : null}
          </form>
        </section>
      )}
    </FinancePage>
  );
}
